import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {updatePassword} from '../store/slices/authThunks';
import {
  authLoadingSelector,
  isAuthenticatedSelector,
  updatePasswordApiSelector,
} from '../store/selectors/auth.selectors';
import AuthCard from './auth/AuthCard';
import ResetPasswordForm, {ResetPasswordValues} from './auth/ResetPasswordForm';
import {primaryButtonClass} from './formStyles';
import {CheckIcon} from './Icons';
import LoadingPageComponent from '../utils/loadingPage.component';
import {useToast} from './Toast';

const BackToSignIn = () => (
  <Link to="/login" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
        ← Back to sign in
  </Link>
);

/**
 * Step 2 of the password reset — magic-link only, no code. The session is already live by the time
 * this mounts: `/auth/confirm` exchanged the emailed link's `token_hash` for one via `verifyOtp`
 * and navigated here, storing `user`/`session` in the same dispatch so there is nothing left to wait
 * on. `authLoading`/`isAuthenticated` are still the right gate — a stale bookmark of this URL, or a
 * session that failed to exchange, looks identical to "not authenticated" and gets the same expired
 * -link screen. There is nothing to ask the user for on the way in — just the new password.
 */
const ResetPasswordContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {showToast} = useToast();

  const authLoading = useAppSelector(authLoadingSelector);
  const isAuthenticated = useAppSelector(isAuthenticatedSelector);
  const updateApi = useAppSelector(updatePasswordApiSelector);

  const [done, setDone] = useState(false);

  const handleSubmit = async (values: ResetPasswordValues) => {
    try {
      await dispatch(updatePassword(values.password)).unwrap();
      setDone(true);
      showToast('Password updated', 'success');
      setTimeout(() => navigate('/welcome', {replace: true}), 1200);
    } catch {
      // The thunk puts its message in the slice; the card renders it.
    }
  };

  if (authLoading) {
    return <LoadingPageComponent loading />;
  }

  if (done) {
    return (
      <AuthCard title="Password updated" subtitle="You're signed in with your new password.">
        <div className="mt-5 flex items-center gap-2 rounded-lg border border-emerald-500/30
         bg-emerald-500/[0.06] px-3 py-2.5 text-sm text-emerald-200">
          <CheckIcon className="w-4 h-4 shrink-0" />
          <span>Taking you into the app…</span>
        </div>
      </AuthCard>
    );
  }

  if (!isAuthenticated) {
    return (
      <AuthCard title="Link expired" subtitle="This password reset link is invalid or has expired." footer={<BackToSignIn />}>
        <Link to="/forgot-password" className={`mt-5 block w-full text-center ${primaryButtonClass}`}>
                    Request a new link
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Set a new password"
      subtitle="Choose a new password for your account."
      error={updateApi.error}
      footer={<BackToSignIn />}
    >
      <ResetPasswordForm submitting={updateApi.status === 'pending'} onSubmit={handleSubmit} />
    </AuthCard>
  );
};

export default ResetPasswordContainer;
